import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header.jsx';
import Footer from './components/Footer.jsx';
import AnnouncementBar from './components/AnnouncementBar.jsx';
import Home from './pages/Home.jsx';
import About from './pages/About.jsx';
import Contact from './pages/Contact.jsx';
import Privacy from './pages/Privacy.jsx';
import Terms from './pages/Terms.jsx';
import Architectural from './pages/Architectural.jsx';
import Commercial from './pages/Commercial.jsx';
import NotFound from './pages/NotFound.jsx';
import FAQ from './pages/FAQ.jsx';
import Resources from './pages/Resources.jsx';
import ResourceDetail from './pages/ResourceDetail.jsx';
import ComingSoon from './pages/ComingSoon.jsx';
import Portfolios from './pages/Portfolios.jsx';
import Products from './pages/Products.jsx';
import AluminumRailings from './pages/products/AluminumRailings.jsx';
import IronRailings from './pages/products/IronRailings.jsx';
import './App.css';

const PageTransitionItem = React.forwardRef(({ announcementVisible, location, ...props }, ref) => {
  const nodeRef = React.useRef(null);
  return (
    <CSSTransition
      {...props}
      nodeRef={nodeRef}
      timeout={300}
      classNames="page"
      unmountOnExit
    >
      <main 
        ref={nodeRef}
        className="main" 
        style={{ marginTop: announcementVisible ? '80px' : '70px' }}
      >
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/:slug" element={<ResourceDetail />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/architectural" element={<Architectural />} />
          <Route path="/commercial" element={<Commercial />} />
          <Route path="/faqs" element={<FAQ />} />
          <Route path="/portfolios" element={<Portfolios />} />
          {/* Products and sub-routes */}
          <Route path="/products" element={<Products />} />
          <Route path="/products/aluminum-railings" element={<AluminumRailings />} />
          <Route path="/products/iron-railings" element={<IronRailings />} />
          <Route path="/products/:slug" element={<ComingSoon />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </CSSTransition>
  );
});

function AnimatedRoutes({ announcementVisible }) {
  const location = useLocation();
  
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  
  return (
    <TransitionGroup component={null}>
      <PageTransitionItem
        key={location.key}
        announcementVisible={announcementVisible}
        location={location}
      />
    </TransitionGroup>
  );
}

function App() {
  const [announcementVisible, setAnnouncementVisible] = useState(true);

  const handleAnnouncementClose = () => {
    setAnnouncementVisible(false);
  };

  return (
    <HelmetProvider>
      <FluentProvider theme={webLightTheme}>
        <Router>
          <div className="container">
            <AnnouncementBar 
              onClose={handleAnnouncementClose} 
              visible={announcementVisible}
            />
            <Header announcementVisible={announcementVisible} />
            <AnimatedRoutes announcementVisible={announcementVisible} />
            <Footer />
          </div>
        </Router>
      </FluentProvider>
    </HelmetProvider>
  );
}

export default App;